import { Directive, input, model, output, ViewChild } from '@angular/core';

import { ActivatedRoute } from '@angular/router';
import { filter, map } from 'rxjs/operators';
import { substr } from 'stringz';

import {
  ConfigurationItem,
  ConfigurationItemResult,
  SemantriaConfiguration,
  TaxonomyTargets,
} from '../../../providers/semantria-api/configuration.service';
import { ConfigurationVersionTrackerService } from '../services/configuration-version-tracker.service';
import { ConfigurationItemEditorComponent } from './configuration-item-editor/configuration-item-editor.component';

export class Highlight {
  offset: number;
  length: number;
  tooltipText?: string;
  offsetType: 'char' | 'byte';
}

@Directive()
export abstract class ConfigurationDetailsTabBase<T extends ConfigurationItem = ConfigurationItem> {
  readonly configuration = input<SemantriaConfiguration>();
  readonly taxonomyItemUsage = input<TaxonomyTargets>();
  readonly editable = input<boolean>();
  readonly configurationLayers = model<ConfigurationItem[]>();

  readonly itemCountChange = output<number>();
  readonly itemCountSet = output<number>();

  @ViewChild(ConfigurationItemEditorComponent, /* TODO: add static flag */ {})
  editor: ConfigurationItemEditorComponent;

  loadItems: (configId) => Promise<ConfigurationItemResult<T>>;
  createItem: (configId, item) => Promise<T>;
  saveItem: (configId, item) => Promise<T>;
  deleteItem: (configId, itemId) => Promise<any>;
  deleteAllItems: (configId) => Promise<any>;
  evaluateItem: () => Promise<any>;
  evaluateDataset: (configId, item) => Promise<any>;
  getEvaluationConfig: (configId) => Promise<string>;
  getHightlightTextString: (doc, documentText, itemValues, lookInSections?) => string;
  getEvaluationItemHighlightValue: (item) => string;
  getItemByClonedFrom: (configId, clonedFrom) => Promise<T>;
  deleteEvaluationConfig: (configId) => Promise<void>;
  itemList: T[];
  itemType: string;

  selectedItemId: string;
  selectedItemName: string;

  protected constructor(
    protected route: ActivatedRoute,
    protected configVersionTracker: ConfigurationVersionTrackerService
  ) {
    this.route.queryParamMap
      .pipe(
        map((params) => params.get('itemName')),
        filter((name) => !!name)
      )
      .subscribe((name) => (this.selectedItemName = name));

    this.route.queryParamMap
      .pipe(
        map((params) => params.get('itemId')),
        filter((id) => !!id)
      )
      .subscribe((id) => (this.selectedItemId = id));
  }

  async checkQueryTopicEdit(resolve) {
    return this.editor.checkItemChanges(resolve);
  }

  cancelEdit() {
    this.editor.cancelEdit();
  }

  get editMode() {
    return this.editor.hasChanges;
  }

  onItemsLoaded($event: T[]) {
    this.itemList = $event;
  }

  onItemAdded(_item) {
    this.itemCountChange.emit(1);
    this.configVersionTracker.reloadConfiguration(this.configuration().id);
  }

  onItemsDeleted(items) {
    this.itemCountChange.emit(-items.length);
    this.configVersionTracker.reloadConfiguration(this.configuration().id);
  }

  onAllItemsDeleted(itemIds) {
    this.itemCountSet.emit(itemIds.length);
    this.configVersionTracker.reloadConfiguration(this.configuration().id);
  }

  onItemUpdated(_item) {
    this.configVersionTracker.reloadConfiguration(this.configuration().id);
  }

  highlightTextStringFromHighlights(highlights: Highlight[], documentText: string): string {
    const escapeHTML = (str: string) =>
      str.replace(
        /[&<>'"]/g,
        (tag) =>
          ({
            '&': '&amp;',
            '<': '&lt;',
            '>': '&gt;',
            "'": '&#39;',
            '"': '&quot;',
          })[tag] || tag
      );

    const fauxEscapeHTML = (str: string) =>
      str.replace(
        /[&<>'"]/g,
        (tag) =>
          ({
            '&': 'XXampYY',
            '<': 'XXltYY',
            '>': 'XXgtYY',
            "'": 'XX#39YY',
            '"': 'XXquotYY',
          })[tag] || tag
      );

    const fauxUnescapeHTML = (str: string) =>
      str.replace(
        /XXampYY|XXltYY|XXgtYY|XX#39YY|XXquotYY/g,
        (tag) =>
          ({
            XXampYY: '&',
            XXltYY: '<',
            XXgtYY: '>',
            'XX#39YY': "'",
            XXquotYY: '"',
          })[tag] || tag
      );

    // Sort collected locations by offset and length
    // Greater offsets come first to maintain offset counter consistency during replace
    highlights.sort((a, b) => {
      const ret = b.offset - a.offset;
      return ret || b.length - a.length;
    });

    let mergedLocations: Highlight[] = [];
    // Merge possibly overlapping locations
    for (const location of highlights) {
      const endOffset = location.offset + location.length;
      const contained = mergedLocations.filter((l) => {
        return (
          (location.offset <= l.offset &&
            endOffset >= l.offset &&
            endOffset <= l.offset + l.length) ||
          (endOffset >= l.offset + l.length &&
            location.offset >= l.offset &&
            location.offset <= l.offset + l.length)
        );
      });
      if (contained.length > 0) {
        for (const containedLocation of contained) {
          if (location.offset < containedLocation.offset) {
            containedLocation.offset = location.offset;
          }
          if (endOffset > containedLocation.offset + containedLocation.length) {
            containedLocation.length = location.length;
          }
        }
      } else {
        mergedLocations.push({ ...location });
      }
    }

    // Remove same locations
    mergedLocations = mergedLocations.reduce((uniqueArray, location) => {
      return uniqueArray.find((uniqueLocation) => {
        return (
          location.offset === uniqueLocation.offset && location.length === uniqueLocation.length
        );
      })
        ? uniqueArray
        : [...uniqueArray, location];
    }, []);

    const encoder = new TextEncoder();
    const decoder = new TextDecoder();

    mergedLocations.forEach((highlight) => {
      const start = highlight.offset;
      const len = highlight.length;

      if (highlight.offsetType === 'char') {
        // highlight based on (char)offset and (char)length
        documentText =
          substr(documentText, 0, start) +
          fauxEscapeHTML(`<span title="${highlight.tooltipText || ''}" class="basic-highlight">`) +
          substr(documentText, start, len) +
          fauxEscapeHTML('</span>') +
          substr(documentText, start + len);
      } else {
        // highlight based on (byte)offset and (byte)length
        const bytes = encoder.encode(documentText);

        documentText =
          decoder.decode(bytes.slice(0, highlight.offset)) +
          fauxEscapeHTML(`<span title="${highlight.tooltipText || ''}" class="basic-highlight">`) +
          decoder.decode(bytes.slice(highlight.offset, highlight.offset + highlight.length)) +
          fauxEscapeHTML('</em>') +
          decoder.decode(bytes.slice(highlight.offset + highlight.length));
      }
    });

    // escape actual HTML from the document
    documentText = escapeHTML(documentText);
    // unescape our faux escaped hightlight html
    documentText = fauxUnescapeHTML(documentText);

    return documentText;
  }

  /**
   * @fixme
   */
  setDocumentHitIcon(hit, docId, hitScore?) {
    const checkMarkItem = document.getElementById('evaluation-checkmark-' + docId);
    if (checkMarkItem) {
      checkMarkItem.classList.remove('configuration-item-editor__evaluation-checkmark--hit');
      checkMarkItem.classList.remove('configuration-item-editor__evaluation-checkmark--miss');
      checkMarkItem.title = '';
      if (hit) {
        checkMarkItem.classList.add('configuration-item-editor__evaluation-checkmark--hit');
        if (hitScore) {
          checkMarkItem.title = 'Score: ' + hitScore;
        }
      } else {
        checkMarkItem.classList.add('configuration-item-editor__evaluation-checkmark--miss');
        checkMarkItem.title = `${this.itemType} did not hit on this document.`;
      }
    }
  }

  canEditItem(): boolean {
    if (this.editor) {
      return this.editor.canEditItem && this.editor.itemsEditable();
    }
    return false;
  }

  canTestItem() {
    return (
      this.configuration() &&
      this.configuration().settings &&
      this.configuration().settings.includes('MENTIONS')
    );
  }
}
